// Shared by the scanner's "Товары и стельки" picker (App.jsx) and the patient card's "Покупки"
// picker (PatientCard.jsx) — one place for the product->picker-item shape and the group tree
// builder so both stay in sync instead of drifting apart.
export function productToPickerItem(p) {
  return {
    id: p.id, name: p.name, price: p.price,
    meta: p.category === "insole"
      ? (p.price ? `${p.price.toLocaleString("ru-RU")} ₽` : null)
      : [p.article, p.price ? `${p.price.toLocaleString("ru-RU")} ₽` : null].filter(Boolean).join(" · ") || null,
    // Insoles no longer carry photos (removed from the catalog UI) — always the emoji fallback.
    thumb: p.category === "insole"
      ? <span className="text-lg">👟</span>
      : <span className="text-lg">✨</span>,
  };
}

const PRODUCT_CATEGORY_LABELS = { insole: "Стельки", accessory: "Сопутствующие" };

function buildCategoryNode(products, groupRows, category) {
  const childrenByParent = new Map();
  groupRows.filter(g => g.category === category).forEach(g => {
    const key = g.parentId || "root";
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(g);
  });
  const productsByGroup = new Map();
  products.filter(p => p.category === category).forEach(p => {
    const key = p.groupId || "root";
    if (!productsByGroup.has(key)) productsByGroup.set(key, []);
    productsByGroup.get(key).push(p);
  });

  // Один и тот же раздел каталога нередко существует несколько раз — общая (location: null)
  // строка плюс отдельная копия на каждой точке продаж (см. product_groups.location) — в пикере
  // это выглядело как несколько НЕОТЛИЧИМЫХ визуально одинаковых папок с одним названием, где
  // клик на одну из копий показывал пусто, а товары лежали в другой. Схлопываем узлы с
  // одинаковым именем на каждом уровне в один, объединяя их подпапки и товары.
  function buildNodesForParent(parentKey) {
    const raw = (childrenByParent.get(parentKey) || [])
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
    const byName = new Map();
    for (const g of raw) {
      if (!byName.has(g.name)) byName.set(g.name, []);
      byName.get(g.name).push(g);
    }
    return [...byName.values()].map(group => {
      const ids = group.map(g => g.id);
      return {
        id: ids[0], name: group[0].name,
        children: ids.flatMap(buildNodesForParent),
        items: ids.flatMap(id => (productsByGroup.get(id) || []).map(productToPickerItem)),
      };
    });
  }

  return {
    id: null, name: PRODUCT_CATEGORY_LABELS[category],
    children: buildNodesForParent("root"),
    items: (productsByGroup.get("root") || []).map(productToPickerItem),
  };
}

// Builds a navigable group tree ({ id, name, children, items }) for CatalogPickerModal's
// drill-down mode — replaces the old flat "Стельки / Ортопедические / Детские" path-label list.
// Top level is always the two categories ("Стельки" / "Сопутствующие"), each holding its own
// group subtree — mirrors how the flat list used to prefix every section with the category name.
export function buildProductGroupTree(products, groupRows) {
  const insole = buildCategoryNode(products, groupRows, "insole");
  const accessory = buildCategoryNode(products, groupRows, "accessory");
  return {
    id: null, name: null,
    children: [
      { id: "cat-insole", name: "Стельки", children: insole.children, items: insole.items },
      { id: "cat-accessory", name: "Сопутствующие", children: accessory.children, items: accessory.items },
    ].filter(c => c.children.length > 0 || c.items.length > 0),
    items: [],
  };
}
